import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIdleTimer } from "react-idle-timer";
import { useSelector } from "react-redux";
import { canReadTimeTracker } from "../../../app/utils/common/Functions";
import {
  checkOutTimeTracker,
  fetchActiveTimeTrackerSession,
  pauseTimeTracker,
  recordTimeTrackerActivity,
  recordTimeTrackerIdleAck,
  recordTimeTrackerIdleStart,
  recordTimeTrackerVisibility,
} from "../../../app/utils/timeTracker/timeTrackerApi";
import { isSessionPaused } from "../../../app/utils/timeTracker/timeTrackerSessionUtils";
import { TIME_TRACKER_SESSION_UPDATED_EVENT } from "../../../app/utils/constants/apiConstant";
import {
  clearTimeTrackerDesktopAlerts,
  showAutoPauseDesktopAlert,
  showIdleDesktopAlert,
} from "../../../app/utils/timeTracker/timeTrackerNotifications";
import {
  isSystemIdleDetectionSupported,
  startSystemIdleDetector,
} from "../../../app/utils/timeTracker/timeTrackerSystemIdle";
import TimeTrackerIdleModal from "../../molecules/TimeTrackerIdleModal/TimeTrackerIdleModal";

const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
const AUTO_PAUSE_TIMEOUT_MS = 2 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 30 * 1000;
/** Soft presence ping while checked in and PC is active (even if dashboard tab is hidden). */
const SYSTEM_ACTIVE_PING_MS = 60 * 1000;

/** Page-level idle falsely fires when the dashboard tab is hidden — prefer OS IdleDetector. */
const PREFER_SYSTEM_IDLE = typeof window !== "undefined" && typeof window.IdleDetector === "function";

const dispatchSessionUpdated = () => {
  window.dispatchEvent(new CustomEvent(TIME_TRACKER_SESSION_UPDATED_EVENT));
};

const TimeTrackerContext = createContext({
  activeSession: null,
  refreshSession: async () => { },
  setActiveSession: () => { },
});

export const useTimeTracker = () => useContext(TimeTrackerContext);

const TimeTrackerProvider = ({ children }) => {
  const user = useSelector((state) => state.auth?.auth?.user);
  const isAuth = useSelector((state) => state.auth?.auth?.isAuth);
  const canTrack = canReadTimeTracker();

  const [activeSession, setActiveSession] = useState(null);
  const [idleModalOpen, setIdleModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);

  const pendingEventsRef = useRef(new Set());
  const lastActivitySentRef = useRef(0);
  const autoPauseTimerRef = useRef(null);
  const isTrackingRef = useRef(false);
  const idleResetRef = useRef(() => { });
  const idlePendingRef = useRef(false);
  const activeSessionRef = useRef(null);
  const systemIdleAbortRef = useRef(null);
  const usingSystemIdleRef = useRef(false);
  const handleIdleRef = useRef(async () => { });
  const handleSystemActiveAgainRef = useRef(() => { });

  const openIdlePrompt = useCallback(() => {
    idlePendingRef.current = true;
    setIdleModalOpen(true);
  }, []);

  const closeIdlePrompt = useCallback(() => {
    idlePendingRef.current = false;
    setIdleModalOpen(false);
    clearTimeTrackerDesktopAlerts();
  }, []);

  const clearAutoPauseTimer = useCallback(() => {
    if (autoPauseTimerRef.current) {
      clearTimeout(autoPauseTimerRef.current);
      autoPauseTimerRef.current = null;
    }
  }, []);

  const isPaused = isSessionPaused(activeSession);
  const trackingEnabled = Boolean(activeSession) && !isPaused;

  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);

  const refreshSession = useCallback(async () => {
    if (!canTrack || !isAuth) {
      setActiveSession(null);
      activeSessionRef.current = null;
      return null;
    }
    try {
      const session = await fetchActiveTimeTrackerSession();
      const hadSession = Boolean(activeSessionRef.current);
      activeSessionRef.current = session;
      setActiveSession(session);
      isTrackingRef.current = Boolean(session) && !isSessionPaused(session);
      if (hadSession && !session) {
        dispatchSessionUpdated();
      }
      return session;
    } catch (_error) {
      activeSessionRef.current = null;
      setActiveSession(null);
      isTrackingRef.current = false;
      return null;
    }
  }, [canTrack, isAuth]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession, user?.id]);

  // Re-check at IST midnight and when the tab becomes visible so overnight
  // paused sessions clear and Check In shows for the new day.
  useEffect(() => {
    if (!canTrack || !isAuth) return undefined;

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshSession();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    let midnightTimerId = null;
    const scheduleMidnightRefresh = () => {
      const now = new Date();
      const istOffsetMs = (5 * 60 + 30) * 60 * 1000;
      const utcMs = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
      const istNow = new Date(utcMs + istOffsetMs);
      const nextMidnightIst = new Date(istNow);
      nextMidnightIst.setHours(24, 0, 5, 0);
      const delay = Math.max(1000, nextMidnightIst.getTime() - istNow.getTime());
      midnightTimerId = setTimeout(() => {
        refreshSession();
        scheduleMidnightRefresh();
      }, delay);
    };
    scheduleMidnightRefresh();

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      if (midnightTimerId) clearTimeout(midnightTimerId);
    };
  }, [canTrack, isAuth, refreshSession]);

  const flushActivity = useCallback(async () => {
    if (!isTrackingRef.current || !pendingEventsRef.current.size) return;
    const eventTypes = Array.from(pendingEventsRef.current);
    pendingEventsRef.current.clear();
    lastActivitySentRef.current = Date.now();
    try {
      const session = await recordTimeTrackerActivity(eventTypes);
      if (session) setActiveSession(session);
    } catch (_error) {
      // Silent fail for background pings
    }
  }, []);

  const queueActivity = useCallback(
    (eventType) => {
      if (!isTrackingRef.current) return;
      pendingEventsRef.current.add(eventType);
      const now = Date.now();
      if (now - lastActivitySentRef.current >= ACTIVITY_THROTTLE_MS) {
        flushActivity();
      }
    },
    [flushActivity]
  );

  const handleAutoPause = useCallback(async () => {
    if (!activeSession || isSessionPaused(activeSession)) return;
    try {
      const session = await pauseTimeTracker();
      if (session) {
        setActiveSession(session);
        isTrackingRef.current = false;
      }
      showAutoPauseDesktopAlert();
      dispatchSessionUpdated();
    } catch (_error) {
      // ignore
    } finally {
      closeIdlePrompt();
      clearAutoPauseTimer();
      idleResetRef.current();
    }
  }, [activeSession, clearAutoPauseTimer, closeIdlePrompt]);

  const handleIdle = useCallback(async () => {
    if (!isTrackingRef.current) return;
    if (idlePendingRef.current) return;
    openIdlePrompt();
    showIdleDesktopAlert({
      onOpenDashboard: () => openIdlePrompt(),
    });
    try {
      await recordTimeTrackerIdleStart();
    } catch (_error) {
      // ignore
    }
    clearAutoPauseTimer();
    autoPauseTimerRef.current = setTimeout(() => {
      handleAutoPause();
    }, AUTO_PAUSE_TIMEOUT_MS);
  }, [clearAutoPauseTimer, handleAutoPause, openIdlePrompt]);

  /** When PC activity resumes (system idle), cancel auto-pause without needing to open the tab. */
  const handleSystemActiveAgain = useCallback(async () => {
    if (!idlePendingRef.current) return;
    clearAutoPauseTimer();
    try {
      const session = await recordTimeTrackerIdleAck();
      if (session) setActiveSession(session);
    } catch (_error) {
      // ignore
    }
    closeIdlePrompt();
    idleResetRef.current();
  }, [clearAutoPauseTimer, closeIdlePrompt]);

  useEffect(() => {
    handleIdleRef.current = handleIdle;
  }, [handleIdle]);

  useEffect(() => {
    handleSystemActiveAgainRef.current = handleSystemActiveAgain;
  }, [handleSystemActiveAgain]);

  const handleIdleConfirm = useCallback(async () => {
    setConfirmLoading(true);
    clearAutoPauseTimer();
    try {
      const session = await recordTimeTrackerIdleAck();
      if (session) setActiveSession(session);
      closeIdlePrompt();
      idleResetRef.current();
    } catch (_error) {
      closeIdlePrompt();
    } finally {
      setConfirmLoading(false);
    }
  }, [clearAutoPauseTimer, closeIdlePrompt]);

  const handleIdlePause = useCallback(async () => {
    setPauseLoading(true);
    clearAutoPauseTimer();
    try {
      const session = await pauseTimeTracker();
      if (session) {
        setActiveSession(session);
        isTrackingRef.current = false;
      }
      closeIdlePrompt();
      idleResetRef.current();
      dispatchSessionUpdated();
    } catch (_error) {
      // ignore
    } finally {
      setPauseLoading(false);
    }
  }, [clearAutoPauseTimer, closeIdlePrompt]);

  const handleIdleCheckOut = useCallback(async () => {
    setCheckoutLoading(true);
    clearAutoPauseTimer();
    try {
      await checkOutTimeTracker();
      setActiveSession(null);
      isTrackingRef.current = false;
      closeIdlePrompt();
      idleResetRef.current();
      dispatchSessionUpdated();
    } catch (_error) {
      // ignore
    } finally {
      setCheckoutLoading(false);
    }
  }, [clearAutoPauseTimer, closeIdlePrompt]);

  const handleIdleClose = useCallback(() => {
    setIdleModalOpen(false);
    clearAutoPauseTimer();
    autoPauseTimerRef.current = setTimeout(() => {
      handleAutoPause();
    }, AUTO_PAUSE_TIMEOUT_MS);
  }, [clearAutoPauseTimer, handleAutoPause]);

  // Prefer Chromium Idle Detection API (PC-level). Fall back to in-page idle timer
  // only when IdleDetector is not available in this browser.
  useEffect(() => {
    if (!canTrack || !trackingEnabled) {
      usingSystemIdleRef.current = false;
      if (systemIdleAbortRef.current) {
        systemIdleAbortRef.current.abort();
        systemIdleAbortRef.current = null;
      }
      return undefined;
    }

    if (!isSystemIdleDetectionSupported()) {
      usingSystemIdleRef.current = false;
      return undefined;
    }

    // Prevent page-level idle from firing while system detector starts / runs.
    usingSystemIdleRef.current = true;
    let cancelled = false;

    const start = async () => {
      const started = await startSystemIdleDetector({
        thresholdMs: IDLE_TIMEOUT_MS,
        onChange: ({ userState, screenState }) => {
          if (!isTrackingRef.current) return;
          // Only treat as idle when the user is idle AND (optionally) screen locked.
          // Do not idle solely because the dashboard tab is in the background.
          if (userState === "idle" || screenState === "locked") {
            handleIdleRef.current();
          } else if (userState === "active") {
            handleSystemActiveAgainRef.current();
            queueActivity("activity");
          }
        },
      });

      if (cancelled) {
        started?.controller?.abort();
        return;
      }

      if (started?.controller) {
        systemIdleAbortRef.current = started.controller;
        usingSystemIdleRef.current = true;
      } else {
        // Permission missing — keep page idle disabled to avoid false alerts in other tabs.
        // User must Allow idle detection on Check In.
        usingSystemIdleRef.current = true;
        systemIdleAbortRef.current = null;
      }
    };

    start();

    return () => {
      cancelled = true;
      usingSystemIdleRef.current = PREFER_SYSTEM_IDLE;
      if (systemIdleAbortRef.current) {
        systemIdleAbortRef.current.abort();
        systemIdleAbortRef.current = null;
      }
    };
  }, [canTrack, trackingEnabled, queueActivity]);

  // While PC is active (system idle mode), keep presence alive even if the dashboard tab is hidden.
  useEffect(() => {
    if (!canTrack || !trackingEnabled) return undefined;
    const interval = setInterval(() => {
      if (!isTrackingRef.current || !usingSystemIdleRef.current) return;
      if (idlePendingRef.current) return;
      queueActivity("activity");
    }, SYSTEM_ACTIVE_PING_MS);
    return () => clearInterval(interval);
  }, [canTrack, trackingEnabled, queueActivity]);

  const { reset } = useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    onIdle: () => {
      // Never use page idle when IdleDetector exists (avoids false idle in other tabs).
      if (PREFER_SYSTEM_IDLE || usingSystemIdleRef.current) return;
      // Hidden dashboard tab is not "PC idle" — only fire while this tab is visible.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      handleIdle();
    },
    onAction: () => {
      if (!isTrackingRef.current) return;
      queueActivity("activity");
    },
    events: [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "click",
      "scroll",
      "wheel",
    ],
    // Disable page idle completely when Chromium IdleDetector is available.
    disabled: !canTrack || !trackingEnabled || PREFER_SYSTEM_IDLE,
    crossTab: true,
    throttle: 500,
  });

  useEffect(() => {
    idleResetRef.current = reset;
  }, [reset]);

  useEffect(() => {
    if (!canTrack || !trackingEnabled) return undefined;

    const onMouseMove = () => queueActivity("mouse");
    const onKeyDown = () => queueActivity("keyboard");
    const onClick = () => queueActivity("click");
    const onScroll = () => queueActivity("scroll");

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("click", onClick, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("click", onClick);
      window.removeEventListener("scroll", onScroll);
    };
  }, [trackingEnabled, canTrack, queueActivity]);

  useEffect(() => {
    if (!canTrack || !trackingEnabled) return undefined;

    const handleVisibility = () => {
      const visible = document.visibilityState === "visible";
      recordTimeTrackerVisibility(visible).catch(() => { });
      if (visible && idlePendingRef.current) {
        openIdlePrompt();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [trackingEnabled, canTrack, openIdlePrompt]);

  useEffect(() => {
    if (!canTrack || !trackingEnabled) return undefined;
    const interval = setInterval(flushActivity, ACTIVITY_THROTTLE_MS);
    return () => clearInterval(interval);
  }, [trackingEnabled, canTrack, flushActivity]);

  useEffect(() => {
    isTrackingRef.current = trackingEnabled;
    if (!activeSession) {
      closeIdlePrompt();
      clearAutoPauseTimer();
    }
    if (isPaused) {
      closeIdlePrompt();
      clearAutoPauseTimer();
      idleResetRef.current();
    }
  }, [activeSession, trackingEnabled, isPaused, clearAutoPauseTimer, closeIdlePrompt]);

  useEffect(
    () => () => {
      clearAutoPauseTimer();
      clearTimeTrackerDesktopAlerts();
      if (systemIdleAbortRef.current) {
        systemIdleAbortRef.current.abort();
        systemIdleAbortRef.current = null;
      }
    },
    [clearAutoPauseTimer]
  );

  const contextValue = useMemo(
    () => ({
      activeSession,
      refreshSession,
      setActiveSession,
    }),
    [activeSession, refreshSession]
  );

  return (
    <TimeTrackerContext.Provider value={contextValue}>
      {children}
      {canTrack ? (
        <TimeTrackerIdleModal
          open={idleModalOpen}
          onConfirm={handleIdleConfirm}
          onPause={handleIdlePause}
          onCheckOut={handleIdleCheckOut}
          onClose={handleIdleClose}
          confirmLoading={confirmLoading}
          pauseLoading={pauseLoading}
          checkoutLoading={checkoutLoading}
        />
      ) : null}
    </TimeTrackerContext.Provider>
  );
};

export default TimeTrackerProvider;
